import { useState, useEffect, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { fetchCorridorItems, deleteAssignment, createAssignment, type CorridorItemsResponse, type OtherBinLocation } from '../../lib/layout-api';
import { useWorkspace } from '../../lib/workspace';
import { OtherBinsCell } from './OtherBinsCell';
import { Trash2, Plus } from 'lucide-react';

interface Props {
  aisle: number;
  zone: string | null; // 'CASHCARRY' | 'LAGER' | null (all)
}

type RowStatus = 'assigned-ok' | 'assigned-missing' | 'unassigned-present';

interface Row {
  bin_code: string;
  zone_code: string;
  item_id: string;
  description: string;
  brand: string;
  category: string;
  display_qty: string;
  unit_price: number;
  assigned: boolean;
  assignment_id: number | null;
  status: RowStatus;
  otherBins: OtherBinLocation[];
}

const STATUS_CFG: Record<RowStatus, { bg: string; border: string; label: string; badgeCls: string }> = {
  'assigned-ok':        { bg: 'bg-green-50',  border: 'border-l-2 border-l-status-green',  label: 'Stokta',    badgeCls: 'bg-green-100 text-green-700' },
  'assigned-missing':   { bg: 'bg-red-50',    border: 'border-l-2 border-l-status-red',    label: 'EKSIK',     badgeCls: 'bg-red-100 text-red-700' },
  'unassigned-present': { bg: 'bg-orange-50', border: 'border-l-2 border-l-status-orange', label: 'Atanmamis', badgeCls: 'bg-orange-100 text-orange-700' },
};

function formatQty(totalBase: number, qtyPerUom: number, salesUom: string, baseUom: string): string {
  if (qtyPerUom <= 1) return `${Math.round(totalBase * 100) / 100} ${baseUom}`;
  const salesQty = Math.floor(totalBase / qtyPerUom);
  const remainder = Math.round((totalBase % qtyPerUom) * 100) / 100;
  if (remainder > 0) return `${salesQty} ${salesUom}, ${remainder} ${baseUom}`;
  return `${salesQty} ${salesUom}`;
}

function buildRows(data: CorridorItemsResponse): Row[] {
  const rows: Row[] = [];
  const otherByItem = new Map<string, OtherBinLocation[]>();
  for (const ob of data.otherBins) {
    if (!otherByItem.has(ob.item_id)) otherByItem.set(ob.item_id, []);
    otherByItem.get(ob.item_id)!.push(ob);
  }

  // Present items
  for (const r of data.items) {
    const others = (otherByItem.get(r.item_id) || []).filter(o => o.bin_code !== r.bin_code);
    rows.push({
      bin_code: r.bin_code, zone_code: r.zone_code,
      item_id: r.item_id, description: r.description,
      brand: r.brand, category: r.category,
      display_qty: formatQty(r.qty_base, r.qty_per_uom, r.sales_uom, r.base_uom),
      unit_price: r.unit_price,
      assigned: r.assigned === 1,
      assignment_id: r.assignment_id,
      status: r.assigned === 1 ? 'assigned-ok' : 'unassigned-present',
      otherBins: others,
    });
  }

  // Missing items
  for (const m of data.missing) {
    const others = (otherByItem.get(m.item_id) || []);
    rows.push({
      bin_code: m.bin_code, zone_code: m.zone_code,
      item_id: m.item_id, description: m.description,
      brand: m.brand, category: m.category,
      display_qty: '—',
      unit_price: 0,
      assigned: true,
      assignment_id: m.assignment_id,
      status: 'assigned-missing',
      otherBins: others,
    });
  }

  return rows;
}

export function CorridorItemList({ aisle, zone }: Props) {
  const { openTab, replaceActiveTab, bottomPanel, setActiveTab } = useWorkspace();
  const [data, setData] = useState<CorridorItemsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await fetchCorridorItems(aisle, zone || undefined)); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [aisle, zone]);

  useEffect(() => { load(); }, [load]);

  function openBinTab(code: string, e?: React.MouseEvent) {
    const tab = { type: 'bin' as const, label: code, params: { binCode: code }, closable: true };
    if (e?.ctrlKey || e?.metaKey) {
      openTab('bottom', { ...tab, id: `bin-${code}-${Date.now()}` });
    } else {
      const existing = bottomPanel.tabs.find(t => t.type === 'bin' && t.params.binCode === code);
      if (existing) {
        setActiveTab('bottom', existing.id);
      } else {
        replaceActiveTab('bottom', tab);
      }
    }
  }

  async function handleRemove(id: number) {
    await deleteAssignment(id); load();
    window.dispatchEvent(new CustomEvent('bin-data-changed'));
  }
  async function handleAssign(binCode: string, itemId: string) {
    await createAssignment(binCode, itemId); load();
    window.dispatchEvent(new CustomEvent('bin-data-changed'));
  }

  if (loading) return <div className="flex items-center justify-center py-8 text-tertiary text-[13px]">Yukleniyor...</div>;
  if (!data) return <div className="flex items-center justify-center py-8 text-status-red text-[13px]">Veri yuklenemedi</div>;

  const rows = buildRows(data);
  const zoneLabel = zone || 'TUM';

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-3 py-1.5 border-b shrink-0" style={{ borderColor: 'rgba(33,35,39,0.07)' }}>
        <span className="text-[13px] font-semibold text-primary">Koridor {aisle}</span>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
          zone === 'CASHCARRY' ? 'bg-orange-100 text-orange-700' :
          zone === 'LAGER' ? 'bg-blue-100 text-blue-700' :
          'bg-gray-100 text-gray-600'
        }`}>{zoneLabel}</span>
        <span className="text-[11px] text-tertiary">{rows.length} urun</span>
        <div className="flex-1" />
        <button onClick={() => load()} className="p-1 text-tertiary hover:text-primary rounded-sm hover:bg-hover" title="Yenile">
          <RefreshCw size={13} />
        </button>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto min-h-0">
        {rows.length > 0 ? (
          <table className="w-full max-w-5xl">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="border-b" style={{ borderColor: 'rgba(33,35,39,0.07)' }}>
                <th className="w-1" />
                <th className="px-3 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-tertiary">Raf</th>
                <th className="px-3 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-tertiary">Urun No</th>
                <th className="px-3 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-tertiary">Aciklama</th>
                <th className="px-3 py-1.5 text-right text-[10px] font-semibold uppercase tracking-wider text-tertiary">Miktar</th>
                <th className="px-3 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-tertiary">Diger Raflar</th>
                <th className="px-3 py-1.5 text-right text-[10px] font-semibold uppercase tracking-wider text-tertiary">Durum</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const cfg = STATUS_CFG[row.status];
                return (
                  <tr key={`${row.bin_code}-${row.item_id}-${i}`}
                    className={`${cfg.bg} border-b hover:brightness-95 transition-all`}
                    style={{ borderColor: 'rgba(33,35,39,0.05)' }}>
                    <td className={cfg.border} />
                    <td className="px-3 py-1.5">
                      <span
                        onClick={e => openBinTab(row.bin_code, e)}
                        className="font-mono text-[11px] text-accent cursor-pointer hover:underline"
                      >{row.bin_code}</span>
                    </td>
                    <td className="px-3 py-1.5 font-mono text-[12px] text-accent">{row.item_id}</td>
                    <td className="px-3 py-1.5 text-[12px] text-primary">{row.description}</td>
                    <td className="px-3 py-1.5 text-[12px] text-right font-semibold text-primary whitespace-nowrap">{row.display_qty}</td>
                    <td className="px-3 py-1.5">
                      <OtherBinsCell bins={row.otherBins} onOpenBin={openBinTab} />
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${cfg.badgeCls}`}>{cfg.label}</span>
                    </td>
                    <td className="px-1.5 py-1.5">
                      {row.zone_code === 'CASHCARRY' && row.assigned && row.assignment_id ? (
                        <button onClick={() => handleRemove(row.assignment_id!)}
                          title="Atamayi kaldir" className="rounded p-1 text-tertiary hover:text-status-red hover:bg-red-50 transition-colors">
                          <Trash2 size={12} />
                        </button>
                      ) : row.zone_code === 'CASHCARRY' && !row.assigned ? (
                        <button onClick={() => handleAssign(row.bin_code, row.item_id)}
                          title="Bu urunu bu rafa ata" className="rounded p-1 text-tertiary hover:text-accent hover:bg-accent-light transition-colors">
                          <Plus size={12} />
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="text-tertiary text-center py-8 text-[13px]">Bu filtrede urun yok.</div>
        )}
      </div>
    </div>
  );
}
